'use client';

import { useState, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';
import {
  Home,
  Building2,
  Users,
  UserPlus,
  Briefcase,
  ClipboardCheck,
  FileText,
  BarChart3,
  ListOrdered,
  MapPin,
  Settings,
  Shield,
  Workflow,
  Wallet,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Sun,
  Moon,
  Bell,
  LogOut,
  User,
} from 'lucide-react';
import { useTheme } from '@/lib/theme/useTheme';
import { cn } from '@/lib/utils';
import { TenantSwitcher } from '@/components/tenant';
import { ImpersonationBanner } from '@/components/layout/ImpersonationBanner';
import {
  navigationItems,
  configurationItems,
  getVisibleNavItems,
  isPathActive,
  type NavigationItem,
} from '@/config/navigation';
import { useTenantUseCases } from '@/hooks/useTenantUseCases';

/**
 * UnifiedAppLayout Component
 *
 * Single unified layout for ALL user types with role-based navigation.
 * Matches the AIadvisorCopilot pattern.
 *
 * Features:
 * - Collapsible sidebar (starts collapsed)
 * - Theme toggle (light/dark mode)
 * - Notification center
 * - User menu with profile/logout
 * - Role-based navigation rendering
 * - Breadcrumbs (optional)
 * - Mobile responsive
 */

export interface UnifiedAppLayoutProps {
  children: React.ReactNode;
  /** Hide breadcrumbs on certain pages */
  hideBreadcrumbs?: boolean;
  /** Custom page title for SEO */
  pageTitle?: string;
  /** Page title to display in header */
  title?: string;
  /** Enterprise Navy theme (Motor Insurance Distribution) */
  enterpriseNavyTheme?: boolean;
}

// Icon mapping for navigation items
const iconMap: Record<string, React.ElementType> = {
  HomeIcon: Home,
  BuildingOfficeIcon: Building2,
  UserGroupIcon: Users,
  UsersIcon: UserPlus,
  UserIcon: User,
  BriefcaseIcon: Briefcase,
  ClipboardDocumentCheckIcon: ClipboardCheck,
  DocumentTextIcon: FileText,
  ChartBarIcon: BarChart3,
  QueueListIcon: ListOrdered,
  MapPinIcon: MapPin,
  CogIcon: Settings,
  ShieldIcon: Shield,
  WorkflowIcon: Workflow,
  WalletIcon: Wallet,
};

export function UnifiedAppLayout({
  children,
  hideBreadcrumbs = false,
  enterpriseNavyTheme = false,
}: UnifiedAppLayoutProps) {
  const pathname = usePathname() || '';
  const router = useRouter();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(true);
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
  const [mounted, setMounted] = useState(false);
  const { theme, setTheme } = useTheme();

  // Mock user data - Replace with actual auth context
  const [user, setUser] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setMounted(true);

    // Read actual user data from localStorage (stored during login)
    const superAdminToken = localStorage.getItem('super_admin_token');
    const accessToken = localStorage.getItem('access_token');
    const userType = localStorage.getItem('user_type');
    const userRole = localStorage.getItem('user_role');
    const userId = localStorage.getItem('user_id');

    // Check for non-empty tokens (empty string means not authenticated)
    const hasSuperAdminToken = superAdminToken && superAdminToken !== '';
    const hasAccessToken = accessToken && accessToken !== '';

    if (hasSuperAdminToken && userType === 'super_admin') {
      setUser({
        id: userId,
        email: 'admin@inspectaone.com',
        userTypeCode: 'SUPER_ADMIN',
        role: userRole || 'admin',
        name: 'Super Admin'
      });
    } else if (hasAccessToken && userType) {
      // Map backend user_type codes to frontend codes
      const userTypeMap: Record<string, string> = {
        'super_admin': 'SUPER_ADMIN',
        'platform_admin': 'PLATFORM_ADMIN',
        'claims_adjuster': 'CLAIMS_ADJUSTER',
        'verifier': 'VERIFIER',
        'insurer_user': 'INSURER_USER',
        'insurer_admin': 'INSURER_ADMIN',
        'partner': 'PARTNER',
        'field_agent': 'FIELD_AGENT',
        'salvage_agency': 'SALVAGE_AGENCY',
        'garage': 'GARAGE',
        'vendor': 'VENDOR'
      };

      const mappedUserType = userTypeMap[userType] || userType.toUpperCase();

      setUser({
        id: userId,
        email: 'user@example.com', // TODO: Store email in localStorage during login
        userTypeCode: mappedUserType,
        role: userRole || 'user',
        name: 'User' // TODO: Store full_name in localStorage during login
      });
    }

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // CRITICAL: Empty array - run once on mount only, not on pathname changes

  const toggleSubmenu = (label: string) => {
    setOpenSubmenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const handleLogout = async () => {
    // Clear localStorage
    localStorage.removeItem('super_admin_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('user_type');
    localStorage.removeItem('user_role');

    // Clear cookies (for middleware)
    document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'refresh_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';

    router.push('/login');
  };

  // Get user type code from user
  const userTypeCode = String(user?.userTypeCode || '');

  // Tenant-scoped use cases gate use-case-restricted nav items (e.g. Pre-Inspections).
  // Returns undefined for super-admin / unscoped sessions, in which case no use-case
  // filter is applied — the route-level UseCaseGate remains the security boundary.
  const { enabledCodes: enabledUseCases } = useTenantUseCases();

  // Filter navigation items based on user type and enabled use cases
  const visibleNavItems = getVisibleNavItems(navigationItems, userTypeCode, enabledUseCases);
  const visibleConfigItems = getVisibleNavItems(configurationItems, userTypeCode, enabledUseCases);

  // Check if any config item is active
  const isConfigActive = visibleConfigItems.some((item) => {
    if (item.href && isPathActive(pathname, item.href)) {
      return true;
    }
    if (item.submenu) {
      return item.submenu.some(subItem => isPathActive(pathname, subItem.href));
    }
    return false;
  });

  // Handle authentication redirect in useEffect to prevent infinite loops
  useEffect(() => {
    if (!loading && !user) {
      router.push('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]); // router omitted intentionally to prevent infinite loops

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show loading while redirecting
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  const renderNavItem = (item: NavigationItem, isCollapsed: boolean = false) => {
    const Icon = typeof item.icon === 'string' ? iconMap[item.icon] : item.icon;

    // Handle submenu items
    if (item.submenu && item.submenu.length > 0) {
      if (isCollapsed) {
        // When collapsed, show only icon linking to first submenu item
        const firstItem = item.submenu[0]!;
        const isSubmenuActive = item.submenu.some(sub => isPathActive(pathname, sub.href));

        return (
          <Link
            key={item.label}
            href={firstItem.href}
            className={cn(
              'flex items-center justify-center rounded-lg py-2 transition-colors',
              !enterpriseNavyTheme && (isSubmenuActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground')
            )}
            title={item.label}
            {...(enterpriseNavyTheme
              ? isSubmenuActive
                ? {
                    'data-navy-active-item': 'true',
                    style: {
                      background: 'var(--active-bg)',
                      color: 'var(--sidebar-text)',
                      borderLeft: '3px solid var(--active-accent)',
                    },
                  }
                : {
                    'data-navy-inactive-text': 'true',
                    style: { color: 'var(--sidebar-text-dim)' },
                  }
              : {})}
          >
            {Icon && <Icon className="h-7 w-7" />}
          </Link>
        );
      }

      const isSubmenuActive = item.submenu.some(sub => isPathActive(pathname, sub.href));
      const isOpen = openSubmenus[item.label] ?? false;

      return (
        <div key={item.label} className="space-y-1">
          <button
            onClick={() => toggleSubmenu(item.label)}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              !enterpriseNavyTheme && (isSubmenuActive
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground')
            )}
            {...(enterpriseNavyTheme
              ? isSubmenuActive
                ? {
                    'data-navy-active-item': 'true',
                    style: {
                      background: 'var(--active-bg)',
                      color: 'var(--sidebar-text)',
                      borderLeft: '3px solid var(--active-accent)',
                    },
                  }
                : {
                    'data-navy-inactive-text': 'true',
                    style: { color: 'var(--sidebar-text-dim)' },
                  }
              : {})}
          >
            {Icon && <Icon className="h-5 w-5" />}
            {item.label}
            {isOpen ? (
              <ChevronDown className="ml-auto h-4 w-4" />
            ) : (
              <ChevronRight className="ml-auto h-4 w-4" />
            )}
          </button>

          {isOpen && (
            <div className="ml-8 space-y-1">
              {item.submenu.map((subItem) => {
                const isActive = isPathActive(pathname, subItem.href);
                return (
                  <Link
                    key={subItem.href}
                    href={subItem.href}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                      !enterpriseNavyTheme && (isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground')
                    )}
                    {...(enterpriseNavyTheme
                      ? isActive
                        ? {
                            'data-navy-active-item': 'true',
                            style: {
                              background: 'var(--active-bg)',
                              color: 'var(--sidebar-text)',
                              borderLeft: '3px solid var(--active-accent)',
                            },
                          }
                        : {
                            'data-navy-inactive-text': 'true',
                            style: { color: 'var(--sidebar-text-dim)' },
                          }
                      : {})}
                  >
                    {subItem.label}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Regular menu item
    if (!item.href) return null;

    const isActive = isPathActive(pathname, item.href);

    return (
      <Link
        key={item.label}
        href={item.href}
        className={cn(
          'flex items-center rounded-lg py-2 text-sm font-medium transition-colors',
          isCollapsed ? 'justify-center px-0' : 'gap-3 px-3',
          !enterpriseNavyTheme && (isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground')
        )}
        title={isCollapsed ? item.label : undefined}
        {...(enterpriseNavyTheme
          ? isActive
            ? {
                'data-navy-active-item': 'true',
                style: {
                  background: 'var(--active-bg)',
                  color: 'var(--sidebar-text)',
                  borderLeft: '3px solid var(--active-accent)',
                },
              }
            : {
                'data-navy-inactive-text': 'true',
                style: { color: 'var(--sidebar-text-dim)' },
              }
          : {})}
      >
        {Icon && <Icon className={cn(isCollapsed ? 'h-7 w-7' : 'h-5 w-5')} />}
        {!isCollapsed && item.label}
      </Link>
    );
  };

  return (
    <div
      className="flex h-screen bg-background overflow-hidden"
      {...(enterpriseNavyTheme ? { 'data-tenant-theme': 'enterprise-navy' } : {})}
    >
      {/* Sidebar - Desktop */}
      <aside
        className={cn(
          'hidden md:flex md:flex-shrink-0 transition-all duration-300 border-r',
          !enterpriseNavyTheme && 'bg-background',
          sidebarCollapsed ? 'w-16' : 'w-64'
        )}
        {...(enterpriseNavyTheme
          ? { 'data-navy-bg': 'true', style: { background: 'var(--sidebar-bg)' } }
          : {})}
      >
        <div className="flex flex-col w-full relative">
          {/* Toggle Button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={cn(
              'absolute top-4 z-10 h-8 w-8 rounded-full border bg-background shadow-sm transition-all hover:bg-accent',
              sidebarCollapsed ? 'right-2' : 'right-2'
            )}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-4 w-4 mx-auto" />
            ) : (
              <ChevronLeft className="h-4 w-4 mx-auto" />
            )}
          </button>

          {/* Logo */}
          <div className="flex h-16 items-center border-b px-4">
            {sidebarCollapsed ? (
              <div
                className={cn(
                  'w-8 h-8 rounded flex items-center justify-center font-bold',
                  !enterpriseNavyTheme && 'bg-primary text-primary-foreground'
                )}
                {...(enterpriseNavyTheme
                  ? {
                      'data-navy-heading': 'true',
                      style: { background: 'var(--active-accent)', color: 'var(--sidebar-bg)' },
                    }
                  : {})}
              >
                I
              </div>
            ) : (
              <h1
                className="text-xl font-bold"
                {...(enterpriseNavyTheme
                  ? { 'data-navy-heading': 'true', style: { color: 'var(--sidebar-text)' } }
                  : {})}
              >
                InspectaOne
              </h1>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden p-4">
            {visibleNavItems.map(item => renderNavItem(item, sidebarCollapsed))}

            {/* Configuration Section */}
            {visibleConfigItems.length > 0 && (
              <div className="pt-4 mt-4 border-t">
                {sidebarCollapsed ? (
                  <Link
                    href={visibleConfigItems[0]!.href || '#'}
                    className={cn(
                      'flex items-center justify-center rounded-lg py-2 transition-colors',
                      !enterpriseNavyTheme && (isConfigActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground')
                    )}
                    title="Configuration"
                    {...(enterpriseNavyTheme
                      ? isConfigActive
                        ? {
                            'data-navy-active-item': 'true',
                            style: {
                              background: 'var(--active-bg)',
                              color: 'var(--sidebar-text)',
                              borderLeft: '3px solid var(--active-accent)',
                            },
                          }
                        : {
                            'data-navy-inactive-text': 'true',
                            style: { color: 'var(--sidebar-text-dim)' },
                          }
                      : {})}
                  >
                    <Settings className="h-7 w-7" />
                  </Link>
                ) : (
                  <>
                    <button
                      onClick={() => setConfigOpen(!configOpen)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        !enterpriseNavyTheme && (isConfigActive
                          ? 'bg-accent text-accent-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground')
                      )}
                      {...(enterpriseNavyTheme
                        ? isConfigActive
                          ? {
                              'data-navy-active-item': 'true',
                              style: {
                                background: 'var(--active-bg)',
                                color: 'var(--sidebar-text)',
                                borderLeft: '3px solid var(--active-accent)',
                              },
                            }
                          : {
                              'data-navy-inactive-text': 'true',
                              style: { color: 'var(--sidebar-text-dim)' },
                            }
                        : {})}
                    >
                      <Settings className="h-5 w-5" />
                      Configuration
                      {configOpen ? (
                        <ChevronDown className="ml-auto h-4 w-4" />
                      ) : (
                        <ChevronRight className="ml-auto h-4 w-4" />
                      )}
                    </button>

                    {configOpen && (
                      <div className="ml-4 mt-1 space-y-1">
                        {visibleConfigItems.map(item => (
                          <div key={item.label}>
                            {item.submenu ? (
                              renderNavItem(item, false)
                            ) : (
                              item.href && (
                                <Link
                                  href={item.href}
                                  className={cn(
                                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                    !enterpriseNavyTheme && (isPathActive(pathname, item.href)
                                      ? 'bg-primary text-primary-foreground'
                                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground')
                                  )}
                                  {...(enterpriseNavyTheme
                                    ? isPathActive(pathname, item.href)
                                      ? {
                                          'data-navy-active-item': 'true',
                                          style: {
                                            background: 'var(--active-bg)',
                                            color: 'var(--sidebar-text)',
                                            borderLeft: '3px solid var(--active-accent)',
                                          },
                                        }
                                      : {
                                          'data-navy-inactive-text': 'true',
                                          style: { color: 'var(--sidebar-text-dim)' },
                                        }
                                    : {})}
                                >
                                  {(() => {
                                    const Icon = typeof item.icon === 'string' ? iconMap[item.icon] : item.icon;
                                    return Icon && <Icon className="h-4 w-4" />;
                                  })()}
                                  {item.label}
                                </Link>
                              )
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </nav>

          {/* Bottom Section - Logout */}
          <div className="border-t p-4">
            <button
              onClick={handleLogout}
              className={cn(
                'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                !enterpriseNavyTheme && 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                sidebarCollapsed && 'justify-center px-0'
              )}
              title={sidebarCollapsed ? 'Logout' : undefined}
              {...(enterpriseNavyTheme
                ? { 'data-navy-inactive-text': 'true', style: { color: 'var(--sidebar-text-dim)' } }
                : {})}
            >
              <LogOut className={cn(sidebarCollapsed ? 'h-7 w-7' : 'h-5 w-5')} />
              {!sidebarCollapsed && 'Logout'}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header
          className={cn('border-b', !enterpriseNavyTheme && 'bg-background')}
          {...(enterpriseNavyTheme
            ? { 'data-navy-bg': 'true', style: { background: 'var(--content-bg)' } }
            : {})}
        >
          <div className="flex items-center justify-between px-4 py-3">
            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-accent"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Right Side - Tenant Switcher, Theme Toggle, Notifications, User */}
            <div className="flex items-center gap-3 ml-auto">
              {/* Tenant Switcher - CR-005 Multi-Tenant Support */}
              <TenantSwitcher />

              {/* Theme Toggle */}
              {mounted && (
                <button
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                  className="p-2 rounded-lg hover:bg-accent transition-colors"
                  title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {theme === 'dark' ? (
                    <Sun className="w-5 h-5" />
                  ) : (
                    <Moon className="w-5 h-5" />
                  )}
                </button>
              )}

              {/* Notifications */}
              <button className="p-2 rounded-lg hover:bg-accent transition-colors relative" title="Notifications">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>

              {/* User Menu */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-accent cursor-pointer transition-colors">
                <User className="w-5 h-5" />
                {!sidebarCollapsed && (
                  <span className="text-sm font-medium hidden sm:inline">{String(user?.name || '')}</span>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Impersonation Banner */}
        <ImpersonationBanner />

        {/* Breadcrumbs */}
        {!hideBreadcrumbs && (
          <div
            className={cn('border-b', !enterpriseNavyTheme && 'bg-background')}
            {...(enterpriseNavyTheme
              ? { 'data-navy-bg': 'true', style: { background: 'var(--content-bg)' } }
              : {})}
          >
            <div className="px-6 py-2">
              <nav className="text-sm text-muted-foreground">
                {/* TODO: Implement breadcrumbs based on pathname */}
                Home / {pathname.split('/').filter(Boolean).join(' / ')}
              </nav>
            </div>
          </div>
        )}

        {/* Page Content */}
        <main
          className={cn('flex-1 overflow-y-auto', !enterpriseNavyTheme && 'bg-muted/30')}
          {...(enterpriseNavyTheme
            ? { 'data-navy-bg': 'true', style: { background: 'var(--content-bg)' } }
            : {})}
        >
          {children}
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside
            className={cn('fixed inset-y-0 left-0 w-64 border-r z-50 md:hidden', !enterpriseNavyTheme && 'bg-background')}
            {...(enterpriseNavyTheme
              ? {
                  'data-navy-bg': 'true',
                  style: { background: 'var(--sidebar-bg)', color: 'var(--sidebar-text)' },
                }
              : {})}
          >
            <div className="flex flex-col h-full">
              {/* Mobile Logo */}
              <div className="flex h-16 items-center border-b px-4 justify-between">
                <h1
                  className="text-xl font-bold"
                  {...(enterpriseNavyTheme ? { 'data-navy-heading': 'true' } : {})}
                >
                  InspectaOne
                </h1>
                <button onClick={() => setMobileMenuOpen(false)}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Mobile Navigation */}
              <nav className="flex-1 space-y-1 overflow-y-auto p-4">
                {visibleNavItems.map(item => renderNavItem(item, false))}
              </nav>

              {/* Mobile Logout */}
              <div className="border-t p-4">
                <button
                  onClick={handleLogout}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    !enterpriseNavyTheme && 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                  {...(enterpriseNavyTheme
                    ? { 'data-navy-inactive-text': 'true', style: { color: 'var(--sidebar-text-dim)' } }
                    : {})}
                >
                  <LogOut className="h-5 w-5" />
                  Logout
                </button>
              </div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}

/**
 * Simplified version without breadcrumbs
 */
export function SimpleAppLayout({ children }: { children: React.ReactNode }) {
  return (
    <UnifiedAppLayout hideBreadcrumbs={true}>
      {children}
    </UnifiedAppLayout>
  );
}
